import type { Attrs, Mark, Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { resolveRemixAlt } from '@atlassian/rovo-content-bridge-api-commands/utils/resolve-remix-alt';

const REMIX_SOURCE_PATTERN = /^remix(?::|$)/u;

/**
 * Builds the attrs and marks for a media node replaced by a Remix result, so both can be applied
 * in the same `setNodeMarkup` (one transaction, one undo step).
 *
 * - `alt` is always set: the new alt, or `''` to clear the previous image's alt (contract C3).
 * - The Remix `dataConsumer` source is added or refreshed (FE-D8). Non-Remix sources and other
 *   marks are kept.
 */
export const buildRemixMediaReplacement = ({
	altText,
	dataConsumerSource,
	mediaAttrs,
	node,
	schema,
}: {
	altText: string | null | undefined;
	dataConsumerSource: string;
	mediaAttrs: Attrs;
	node: PMNode;
	schema: Schema;
}): { attrs: Attrs; marks: readonly Mark[] } => {
	const attrs = { ...node.attrs, ...mediaAttrs, alt: resolveRemixAlt(altText) ?? '' };

	const dataConsumerType = schema.marks?.dataConsumer;
	if (!dataConsumerType) {
		return { attrs, marks: node.marks };
	}

	const existing = dataConsumerType.isInSet(node.marks);
	const otherSources: string[] = (existing?.attrs.sources ?? []).filter(
		(source: string) => !REMIX_SOURCE_PATTERN.test(source),
	);
	const remixMark = dataConsumerType.create({ sources: [...otherSources, dataConsumerSource] });
	const marksWithoutDataConsumer = existing ? existing.removeFromSet(node.marks) : node.marks;

	return { attrs, marks: remixMark.addToSet(marksWithoutDataConsumer) };
};
